import {inject, Injectable} from '@angular/core';
import {Router, UrlTree} from '@angular/router';
import {SimpleCheck} from '../../api';
import {CheckTypePipe} from '../../pipes/check-type.pipe';

export type CheckReportStatus = 'COMPLETED' | 'RUNNING' | 'FAILED' | 'ARCHIVED';

/** Prüftypen der Berichtsübersicht – Badge im myChecks-Tooltip und Filter „Prüftyp“. */
export type ReportRuleTypeLabel = 'IDS Check' | 'KI Check' | 'Hybrider Check' | 'Cross Check';

export const REPORT_RULE_TYPE_LABELS: ReportRuleTypeLabel[] = ['IDS Check', 'KI Check', 'Hybrider Check', 'Cross Check'];

/** Datumsgruppe der Kartenansicht; `label` ist die fertige Überschrift. */
export interface ReportDateGroup<T> {
  key: 'today' | 'lastWeek' | 'older';
  label: string;
  items: T[];
}

/**
 * Das Datum, das der Start-Dialog bis 10/2026 automatisch an den Berichtsnamen hängte
 * (`<myCheck> – dd.MM.yyyy`, siehe `StepCombinedSelectComponent.defaultReportName`). Die Übersicht
 * zeigt das Datum in einer eigenen Spalte; bei Bestandsläufen wird genau dieses Suffix abgeschnitten.
 */
const AUTO_DATE_SUFFIX = /\s+–\s+\d{2}\.\d{2}\.\d{4}$/;

/**
 * Gemeinsame Ableitungen für ausgeführte Prüfläufe (`SimpleCheck`): Anzeigename, Status und
 * Zielroute des Berichts. Genutzt von der Berichte-Übersicht und der Dashboard-Kachel, damit
 * beide Views identisch benennen, einfärben und verlinken.
 */
@Injectable({providedIn: 'root'})
export class CheckReportService {
  private router = inject(Router);
  private checkTypePipe = new CheckTypePipe();

  /**
   * Anzeigename eines Laufs. Kein Rückfall auf den Projektnamen: Alt-Checks (All-In / All-Right) tragen
   * keinen eigenen Namen, damit stand in jeder ihrer Zeilen derselbe Projektname. Der Prüftyp ist für
   * diese Läufe die einzige sinnvolle Bezeichnung – den Namen selbst liefert das Backend inzwischen mit.
   */
  getCheckName(check: SimpleCheck): string {
    return check.checkName || this.getCheckTypeLabel(check);
  }

  /**
   * Name für die Berichtsübersicht: wie {@link getCheckName}, aber ohne das früher automatisch
   * angehängte Startdatum – das steht dort in einer eigenen Spalte. Ein Name, der nur aus dem
   * Datum bestünde, bleibt unverändert.
   */
  getOverviewName(check: SimpleCheck): string {
    const name = this.getCheckName(check);
    const stripped = name.replace(AUTO_DATE_SUFFIX, '');
    return stripped.trim() ? stripped : name;
  }

  /**
   * Prüftyp-Label eines myChecks. `type` ist der Regelset-Typ aus dem Lauf (`IDS`, `KI`, `HYBRID`,
   * `CROSS`) oder – für Läufe ohne Zusammenfassung – der `checkRuleType` des Berichts (`AI` statt `KI`).
   */
  getRuleTypeLabel(type: string | undefined | null): ReportRuleTypeLabel | null {
    switch (type) {
      case 'IDS':
        return 'IDS Check';
      case 'KI':
      case 'AI':
        return 'KI Check';
      case 'HYBRID':
        return 'Hybrider Check';
      case 'CROSS':
        return 'Cross Check';
      default:
        return null;
    }
  }

  /**
   * Prüftypen eines Laufs: aus den ausgeführten myChecks, sonst aus dem `checkRuleType` des
   * Berichts. Ein gemischter Lauf hat mehrere.
   */
  getRuleTypeLabels(check: SimpleCheck): ReportRuleTypeLabel[] {
    const types = check.myChecks?.length
      ? check.myChecks.map(myCheck => myCheck.type)
      : check.checkType === 'MY_CHECK' ? [check.checkRuleType] : [];
    const labels = types
      .map(type => this.getRuleTypeLabel(type))
      .filter((label): label is ReportRuleTypeLabel => label !== null);
    return Array.from(new Set(labels));
  }

  /**
   * Gruppiert nach Heute / Letzte Woche (die sieben Tage vor heute) / Älter. Die Reihenfolge der
   * Einträge bleibt erhalten; leere Gruppen fallen weg.
   */
  groupByDate<T>(items: T[], dateOf: (item: T) => Date | undefined, now: Date = new Date()): ReportDateGroup<T>[] {
    const today = this.startOfDay(now);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - 7);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const groups: ReportDateGroup<T>[] = [
      {key: 'today', label: `Heute · ${this.formatDay(today)}`, items: []},
      {key: 'lastWeek', label: `Letzte Woche · ${this.formatRange(weekStart, yesterday)}`, items: []},
      {key: 'older', label: 'Älter', items: []},
    ];
    for (const item of items) {
      const time = this.timeOf(dateOf(item));
      if (time >= today.getTime()) {
        groups[0].items.push(item);
      } else if (time >= weekStart.getTime()) {
        groups[1].items.push(item);
      } else {
        groups[2].items.push(item);
      }
    }
    return groups.filter(group => group.items.length > 0);
  }

  private startOfDay(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  }

  private formatDay(date: Date): string {
    return `${this.pad(date.getDate())}.${this.pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  }

  /** `21.–27.09.2026`, über Monats- bzw. Jahresgrenzen `28.09.–04.10.2026` bzw. `29.12.2025–04.01.2026`. */
  private formatRange(from: Date, to: Date): string {
    const end = this.formatDay(to);
    if (from.getFullYear() !== to.getFullYear()) return `${this.formatDay(from)}–${end}`;
    if (from.getMonth() !== to.getMonth()) return `${this.pad(from.getDate())}.${this.pad(from.getMonth() + 1)}.–${end}`;
    return `${this.pad(from.getDate())}.–${end}`;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  /** Archivierte Läufe bleiben aussen vor – analog zur Vorbelegung der Berichte-Übersicht. */
  activeRuns(checks: SimpleCheck[]): SimpleCheck[] {
    return checks.filter(check => this.getStatus(check) !== 'ARCHIVED');
  }

  /** Läufe der letzten `days` Tage, ohne archivierte. */
  runsWithinDays(checks: SimpleCheck[], days: number): SimpleCheck[] {
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - days);
    return this.activeRuns(checks).filter(check => {
      if (!check.checkDate) return false;
      const date = new Date(check.checkDate);
      return !isNaN(date.getTime()) && date >= threshold;
    });
  }

  countByStatus(checks: SimpleCheck[], status: CheckReportStatus): number {
    return checks.filter(check => this.getStatus(check) === status).length;
  }

  /** Neueste Läufe zuerst, ohne archivierte, gekappt auf `limit`. */
  latestRuns(checks: SimpleCheck[], limit: number): SimpleCheck[] {
    return [...this.activeRuns(checks)]
      .sort((a, b) => this.timeOf(b.checkDate) - this.timeOf(a.checkDate))
      .slice(0, limit);
  }

  private timeOf(date: Date | undefined): number {
    if (!date) return 0;
    const time = new Date(date).getTime();
    return isNaN(time) ? 0 : time;
  }

  getCheckTypeLabel(check: SimpleCheck): string {
    return check.checkTypeLabel
      || this.checkTypePipe.transform(check.checkType!, check.checkRuleType)
      || '-';
  }

  getStatus(check: SimpleCheck): CheckReportStatus {
    if (check.reportStatus === 'COMPLETED'
      || check.reportStatus === 'RUNNING'
      || check.reportStatus === 'FAILED'
      || check.reportStatus === 'ARCHIVED') {
      return check.reportStatus;
    }
    if (check.archived) return 'ARCHIVED';
    if (check.failed) return 'FAILED';
    if (check.completed) return 'COMPLETED';
    return 'RUNNING';
  }

  getStatusLabel(check: SimpleCheck): string {
    return check.reportStatusLabel || this.getDefaultStatusLabel(this.getStatus(check));
  }

  getStatusBadgeType(check: SimpleCheck): 'success' | 'warning' | 'error' | 'info' {
    switch (this.getStatus(check)) {
      case 'COMPLETED':
        return 'success';
      case 'RUNNING':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'ARCHIVED':
        return 'info';
    }
  }

  /** Sortierreihenfolge: erst was Aufmerksamkeit braucht, dann Abgeschlossenes. */
  getStatusOrder(check: SimpleCheck): number {
    switch (this.getStatus(check)) {
      case 'FAILED':
        return 0;
      case 'RUNNING':
        return 1;
      case 'COMPLETED':
        return 2;
      case 'ARCHIVED':
        return 3;
    }
  }

  getWorkflowInstanceId(check: SimpleCheck): string | undefined {
    return check.workflowInstanceId || check.checkId || (check.checkType === 'MY_CHECK' ? check.id : undefined);
  }

  /** Ein Lauf hat ein Log, sobald eine Workflow-Instanz dahinter steht (alle MY_CHECK-Typen). */
  hasLogView(check: SimpleCheck): boolean {
    return !!this.getWorkflowInstanceId(check);
  }

  canOpenReport(check: SimpleCheck): boolean {
    return this.getStatus(check) !== 'FAILED';
  }

  createLogRoute(instanceId: string, projectId: number): UrlTree {
    return this.router.createUrlTree(
      ['projects', projectId, 'check-runs', 'log'],
      {queryParams: {instanceId}}
    );
  }

  /** Zielroute des Berichts – abhängig von Prüftyp und Status. `null`, wenn es (noch) keine gibt. */
  resolveCheckRoute(check: SimpleCheck, projectId: number): UrlTree | null {
    switch (check.checkType) {
      case 'HOAI':
        // Nur noch der fertige Bericht: die alten Ladeseiten sind mit der myCheck-Umstellung
        // entfallen, neue HOAI-Prüfungen laufen als Prüflauf. `null` heißt "noch keine Route".
        return check.completed
          ? this.router.createUrlTree(['projects', projectId, 'all-in-check', 'hoai', check.id])
          : null;
      case 'QUALITY':
        return check.completed
          ? this.router.createUrlTree(['projects', projectId, 'quality', check.id, 'report'])
          : this.router.createUrlTree(['projects', projectId, 'quality', check.id, 'loading']);
      case 'IFC':
        // LOIplus-Check (LOI wie IDS): eigener Berichtsbereich unter all-in-check. Die generische
        // myCheck-Berichtsseite laedt CheckResults und kennt IFCCheck-Ids nicht.
        if (check.completed) {
          return this.router.createUrlTree(['projects', projectId, 'all-in-check', 'bim-check', check.id, 'base-check']);
        }
        // Wie bei HOAI: die alte Ladeseite ist entfallen.
        return null;
      case 'SWM':
        return check.completed
          ? this.router.createUrlTree(['projects', projectId, 'fireprotection', check.id])
          : null;
      case 'MY_CHECK': {
        const status = this.getStatus(check);
        if ((check.completed || status === 'ARCHIVED') && status !== 'FAILED' && this.getReportId(check)) {
          // All MY_CHECK types (IDS, HYBRID, AI) use the generic report page
          return this.router.createUrlTree(
            ['projects', projectId, 'reports', this.getReportId(check)],
            {queryParams: {type: check.checkRuleType ?? 'HYBRID', checkName: this.getCheckName(check)}}
          );
        }
        const instanceId = this.getWorkflowInstanceId(check);
        if (status === 'FAILED' && instanceId) {
          // Failed checks do not have a report – route to the log view instead.
          return this.createLogRoute(instanceId, projectId);
        }
        if (instanceId) {
          // Check is still running – navigate to execution view to show progress (loading screen), not the log
          return this.router.createUrlTree(
            ['projects', projectId, 'check-runs', 'execute'],
            {queryParams: {instanceId}}
          );
        }
        return null;
      }
      default:
        return null;
    }
  }

  getReportId(check: SimpleCheck): string | undefined {
    if (check.checkRuleType === 'IDS') {
      return check.checkResultId || check.id;
    }
    return check.workflowInstanceId || check.checkResultId || check.id;
  }

  private getDefaultStatusLabel(status: CheckReportStatus): string {
    switch (status) {
      case 'COMPLETED':
        return 'Abgeschlossen';
      case 'RUNNING':
        return 'Prüfung läuft';
      case 'FAILED':
        return 'Fehlgeschlagen';
      case 'ARCHIVED':
        return 'Archiviert';
    }
  }
}
